import { unwrap } from "@sagot/api-client";
import { AUDIT_PAGE_SIZE, consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, ErrorState, Icon, Skeleton, buttonClasses } from "@sagot/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { usePermissions } from "../../../../session/permissions";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceContext, useWorkspaceId } from "../../../../session/workspace-context";
import { useFormatters } from "../../shared";

const K = "privacyM29.binding";
const WINDOW_DAYS = 30;
const DAY_MS = 86_400_000;
const LOOKUP_ACTION = "customer.lookup";

/** The `customer.lookup` rows of the last 30 days (one page): how many lookups ran and when the last one did. */
function useLookupActivity(enabled: boolean) {
  const api = useApi();
  const wsId = useWorkspaceId();
  const [day] = useState(() => new Date(Date.now() - WINDOW_DAYS * DAY_MS).toISOString().slice(0, 10));
  return useQuery({
    queryKey: ["ws", wsId, "settings", "binding-lookups", day] as const,
    queryFn: () => unwrap(api.GET("/v1/audit-logs", { params: { query: { action: LOOKUP_ACTION, since: `${day}T00:00:00.000Z`, limit: AUDIT_PAGE_SIZE } } })),
    enabled,
  });
}

function Header() {
  const { t } = useTranslation();
  return (
    <div className="card-header flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h2 id="binding-title" className="card-title">
          {t(`${K}.title`)}
        </h2>
        <p className="mt-1 text-xs text-fg-muted">{t(`${K}.intro`)}</p>
      </div>
      <Badge tone="brand" dot data-testid="binding-badge">
        {t(`${K}.badge`)}
      </Badge>
    </div>
  );
}

function LiveState({ canView }: { canView: boolean }) {
  const { t } = useTranslation();
  const format = useFormatters();
  const lookups = useLookupActivity(canView);
  if (!canView) return <p className="text-xs text-fg-muted">{t(`${K}.needsAudit`)}</p>;
  if (lookups.isPending) {
    return (
      <div className="space-y-2" role="status" aria-label={t("common.loading")}>
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/3" />
      </div>
    );
  }
  if (lookups.isError) {
    return <ErrorState headingLevel={3} title={t(`${K}.failedTitle`)} description={t(`${K}.failedBody`)} onRetry={() => void lookups.refetch()} retryLabel={t("common.retry")} />;
  }
  const rows = lookups.data.items;
  if (rows.length === 0) {
    return (
      <div className="flex items-start gap-3 rounded-md bg-surface-2 p-3" data-testid="binding-none">
        <Icon name="search" className="mt-0.5 size-4 shrink-0 text-primary-ink" />
        <div>
          <div className="text-sm font-semibold">{t(`${K}.noneTitle`)}</div>
          <p className="text-xs text-fg-muted">{t(`${K}.noneBody`)}</p>
        </div>
      </div>
    );
  }
  const capped = lookups.data.next_cursor !== null;
  return (
    <div className="space-y-1" data-testid="binding-live">
      <div className="text-xs font-semibold text-fg-muted">{t(`${K}.liveLabel`)}</div>
      <p className="text-sm font-semibold" data-testid="binding-count">
        {capped ? t(`${K}.countMore`, { count: rows.length }) : t(`${K}.count`, { count: rows.length })}
      </p>
      <p className="text-xs text-fg-muted">{t(`${K}.latest`, { when: format.dateTime(rows[0]?.occurred_at ?? "") })}</p>
    </div>
  );
}

/** Settings > Privacy > "Lookups use the verified customer" (privacy-security pin 2, PDI-006): identity fields are locked to the session, with the live lookup activity and a link to the log. */
export function IdentityBindingCard() {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const canView = usePermissions().can("audit.view");
  return (
    <section className="card" aria-labelledby="binding-title" data-testid="binding-card">
      <Header />
      <div className="space-y-4 px-5 py-4">
        <div className="flex items-start gap-3 rounded-md border border-line bg-primary-soft p-3">
          <Icon name="lock" className="mt-0.5 size-4 shrink-0 text-primary-ink" />
          <div>
            <div className="text-sm font-semibold">{t(`${K}.lockedTitle`)}</div>
            <p className="mt-0.5 text-xs text-fg-muted">{t(`${K}.lockedBody`)}</p>
          </div>
        </div>
        <LiveState canView={canView} />
      </div>
      {canView ? (
        <div className="flex flex-wrap justify-end gap-2 border-t border-line px-5 py-3">
          <AppLink href={`${consoleRoutes.settings(summary.slug, "audit")}?trail=customer`} className={buttonClasses({ variant: "secondary" })} data-testid="binding-log-link">
            <Icon name="scroll-text" />
            {t(`${K}.viewLog`)}
          </AppLink>
        </div>
      ) : null}
    </section>
  );
}
